import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@ngneat/transloco';
import { Subject, takeUntil, timer } from 'rxjs';

export interface SessionExpiryDialogData {
    /** Absolute expiry of the access token, epoch seconds. */
    expiresAt: number;
}

/** Result emitted when the dialog closes. */
export type SessionExpiryDialogResult = 'logout' | 'expired';

/**
 * Shown only when the session could not be renewed silently.
 * Counts down to the real token expiry and closes itself when it reaches zero.
 */
@Component({
    selector: 'session-expiry-dialog',
    templateUrl: './session-expiry-dialog.component.html',
    styles: [
        `
            .fuse-confirmation-dialog-panel {
                @screen md {
                    @apply w-128;
                }

                .mat-mdc-dialog-container {
                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    imports: [MatButtonModule, MatDialogModule, MatIconModule, TranslocoModule],
})
export class SessionExpiryDialogComponent implements OnInit, OnDestroy {
    data: SessionExpiryDialogData = inject(MAT_DIALOG_DATA);

    private _dialogRef = inject(MatDialogRef<SessionExpiryDialogComponent>);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /** Remaining seconds, clamped at 0. */
    remaining: number = 0;

    ngOnInit(): void {
        this.remaining = this.calcRemaining();

        timer(0, 1000)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.remaining = this.calcRemaining();

                if (this.remaining <= 0) {
                    this._dialogRef.close('expired' as SessionExpiryDialogResult);
                }
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /** Remaining time as m:ss */
    get countdown(): string {
        const mins = Math.floor(this.remaining / 60);
        const secs = Math.floor(this.remaining % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    logout(): void {
        this._dialogRef.close('logout' as SessionExpiryDialogResult);
    }

    private calcRemaining(): number {
        const remaining = this.data.expiresAt - (new Date().getTime() / 1000);
        return remaining > 0 ? remaining : 0;
    }
}
